/**
 * SceneView — renderer, lights, camera behaviour.
 *
 * Two camera modes, because the game has two jobs:
 *  - `follow`: raiding. Behind and above the figure, damped, so a phone player
 *    never has to steer a camera. It also keeps the whole room legible, which
 *    matters more than immersion: the room *is* the joke and you have to be
 *    able to read it.
 *  - `overview`: building and watching replays. The whole dollhouse in frame,
 *    tilted like a diorama on a table.
 *
 * Cost caps live here: pixel ratio ≤ 2, one shadow-casting light, one 1024
 * shadow map sized exactly to the room.
 */

import * as THREE from 'three'

import { ROOM_CONFIG } from '../core/room'
import type { RaiderState } from '../sim/types'

export type CameraMode = 'follow' | 'overview'

const SKY = 0xf6ead6

export class SceneView {
  readonly scene = new THREE.Scene()
  readonly camera: THREE.PerspectiveCamera
  readonly renderer: THREE.WebGLRenderer
  private sun: THREE.DirectionalLight
  private target = new THREE.Vector3()
  private desired = new THREE.Vector3()
  private mode: CameraMode = 'overview'
  private roomWidth: number = ROOM_CONFIG.width
  private roomDepth: number = ROOM_CONFIG.depth
  private onResize = () => this.resize()

  constructor(private host: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
    })
    // Fill rate is the budget on the phones this has to run on.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.setClearColor(SKY, 1)
    host.appendChild(this.renderer.domElement)

    this.scene.background = new THREE.Color(SKY)
    this.scene.fog = new THREE.Fog(SKY, 22, 44)

    this.camera = new THREE.PerspectiveCamera(46, 1, 0.1, 90)
    this.camera.position.set(0, 12, -12)

    // Dev-only: lets tools/raid-check.mjs assert on the real scene graph and the real projection —
    // whether the skinned character replaced the procedural figure, and whether pressing W moves
    // the raider *up the screen*. Neither is visible to a unit test.
    if (import.meta.env.DEV) {
      const w = window as unknown as Record<string, unknown>
      w.__trScene = this.scene
      w.__trCamera = this.camera
    }

    const hemi = new THREE.HemisphereLight(0xfff4e2, 0xcbb392, 0.62)
    this.scene.add(hemi)

    this.sun = new THREE.DirectionalLight(0xfff0d4, 1.25)
    this.sun.position.set(6, 12, -5)
    this.sun.castShadow = true
    this.sun.shadow.mapSize.set(1024, 1024)
    const cam = this.sun.shadow.camera
    const reach = (Math.max(ROOM_CONFIG.width, ROOM_CONFIG.depth) * ROOM_CONFIG.cell) / 2 + 3
    cam.left = -reach
    cam.right = reach
    cam.top = reach
    cam.bottom = -reach
    cam.near = 1
    cam.far = 40
    this.sun.shadow.bias = -0.0012
    this.sun.shadow.normalBias = 0.02
    this.scene.add(this.sun, this.sun.target)

    window.addEventListener('resize', this.onResize)
    this.resize()
  }

  setRoomSize(width: number, depth: number): void {
    this.roomWidth = width
    this.roomDepth = depth
  }

  setMode(mode: CameraMode): void {
    this.mode = mode
  }

  resize(): void {
    const w = this.host.clientWidth || window.innerWidth
    const h = this.host.clientHeight || window.innerHeight
    this.renderer.setSize(w, h, false)
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
  }

  /** Frame the whole room. Portrait phones need to back off further. */
  private overviewPose(): { pos: THREE.Vector3; look: THREE.Vector3 } {
    const span = Math.max(this.roomWidth, this.roomDepth) * ROOM_CONFIG.cell
    const portrait = this.camera.aspect < 1
    // Portrait phones need both more distance and a steeper angle: a shallow
    // diorama view wastes the top half on sky and clips the near row of props.
    const dist = span * (portrait ? 1.36 : 1.0)
    // The camera sits on the **−Z** side, i.e. behind the door the raider comes in through. That
    // sign is load-bearing, not taste: it is what makes screen-up equal world +Z, which is the
    // contract `RaidControls` relies on to skip any camera transform (W → +Z → up the screen,
    // D → +X → right). With the camera on +Z the whole stick was silently rotated 180°.
    // Aim slightly *short* of the room's centre, toward the door. A tilted camera gives the near
    // edge less margin than the far one, and the near edge is now the door — which has to stay on
    // screen, because "the door locks" is a beat the player has to see. It also spends the band of
    // empty floor that would otherwise sit above the far wall in portrait.
    const bias = -this.roomDepth * ROOM_CONFIG.cell * 0.1
    return {
      pos: new THREE.Vector3(0, dist * (portrait ? 1.0 : 0.86), bias - dist * (portrait ? 0.66 : 0.78)),
      look: new THREE.Vector3(0, 0, bias),
    }
  }

  /**
   * Follow pose — deliberately *fixed orientation*, only the position drifts
   * toward the raider.
   *
   * Two reasons this beats an over-the-shoulder camera here:
   *  1. Screen-up is always world +Z, so a thumbstick needs no mental rotation
   *     and there is no feedback loop between turning and the camera turning.
   *  2. The room is the joke. A spectator three seconds into a clip has to be
   *     able to see the trap *and* the victim, which means keeping most of the
   *     dollhouse in frame instead of filling it with the back of a head.
   */
  private followPose(raider: RaiderState): { pos: THREE.Vector3; look: THREE.Vector3 } {
    const overview = this.overviewPose()
    const pull = 0.4
    const look = new THREE.Vector3(raider.x * pull, 0.3, raider.z * pull)
    const pos = overview.pos.clone().multiplyScalar(0.93).add(look)
    const limitX = (this.roomWidth * ROOM_CONFIG.cell) / 2 + 6
    const limitZ = (this.roomDepth * ROOM_CONFIG.cell) / 2 + 10
    pos.x = THREE.MathUtils.clamp(pos.x, -limitX, limitX)
    pos.z = THREE.MathUtils.clamp(pos.z, -limitZ, limitZ)
    return { pos, look }
  }

  /** `dt` drives camera damping; `raider` may be null in overview mode. */
  update(dt: number, raider: RaiderState | null): void {
    const pose = this.mode === 'follow' && raider ? this.followPose(raider) : this.overviewPose()
    this.desired.copy(pose.pos)
    const lag = 1 - Math.exp(-dt * (this.mode === 'follow' ? 7 : 4))
    this.camera.position.lerp(this.desired, lag)
    this.target.lerp(pose.look, lag)
    this.camera.lookAt(this.target)

    // Keep the sun above the action so the room's shadows stay readable, and
    // update its world matrix in the same frame we move it — a shadow matrix
    // read one frame late is the classic source of shimmer.
    const cx = raider && this.mode === 'follow' ? raider.x * 0.35 : 0
    const cz = raider && this.mode === 'follow' ? raider.z * 0.35 : 0
    // Sun on the camera's side (now −Z) so shadows still fall away from the viewer instead of
    // hiding behind the objects that cast them.
    this.sun.position.set(cx + 6, 12, cz - 5)
    this.sun.target.position.set(cx, 0, cz)
    this.sun.target.updateMatrixWorld()
    this.sun.updateMatrixWorld()
  }

  render(): void {
    this.renderer.render(this.scene, this.camera)
  }

  /** Screen point → the cell-plane point under it. Used by the builder. */
  pickGroundPoint(clientX: number, clientY: number): THREE.Vector3 | null {
    const rect = this.renderer.domElement.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    )
    const ray = new THREE.Raycaster()
    ray.setFromCamera(ndc, this.camera)
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
    const hit = new THREE.Vector3()
    return ray.ray.intersectPlane(plane, hit) ? hit : null
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize)
    this.renderer.dispose()
    this.renderer.domElement.remove()
  }
}
